import React, { useCallback } from 'react';
import {
  SwipeclockUserProvider,
  getUrlParameter,
  removeQueryStringKeyFromURL,
} from 'swipeclock-frontend';
import { publish, subscribe } from 'shuttle';
import {
  setCompanyInformation,
  setLogonUrl,
  setPartnerInformation,
  setAdminInformation,
  useAgreements,
} from 'features/bootstrap';
import { setMasterData, setMetricsData } from 'store/swipeclockUserSlice';
import {
  setPrivatePageTitle,
  setPrivatePartnerPageTitle,
} from 'helpers/documentHelper';

import PropTypes from 'prop-types';
import config from 'data/config';
import { setDisableNavigation } from 'store/navigationSlice';
import { setUser } from 'store/userSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function SwipeclockUserBootstrap({ children, name }) {
  const bootstrapWorkforceHub = useBootstrapWorkforceHub();
  const bootstrapPartnerPortal = useBootstrapPartnerPortal();
  const bootstrapSysAdmin = useBootstrapSysAdmin();

  const navigate = useNavigate();
  const handleInitializationCallback = useCallback(
    async ({ masterData, billingData, metrics, user }, onFinishCallback) => {
      if (user.invalidToken) {
        navigate('/error?status=401');
      }

      switch (name) {
        case 'workforcehub':
          bootstrapWorkforceHub({ user, masterData, billingData, metrics });
          break;
        case 'providerportal':
          bootstrapPartnerPortal({ user, masterData, metrics });
          break;
        case 'sysadmin':
          bootstrapSysAdmin({ user, masterData, metrics });
          break;
        default:
          break;
      }

      cleanUpURL();
      // We're all finished setting up the web app
      // so make sure to call the onFinishCallback
      // To let Swipeclock know we're ready to go
      return onFinishCallback({ name });
    },
    []
  );

  const params = {
    amplitudeWFHApiKey: config.amplitudeWFHApiKey,
    appEnv: config.appEnv,
    name,
    masterDataServiceURL: config.masterDataServiceURL,
    billingServiceURL: config.billingServiceUrl,
    onInitializationCallback: handleInitializationCallback,
    publish,
    subscribe,
  };

  return (
    <SwipeclockUserProvider providerParams={params}>
      {children}
    </SwipeclockUserProvider>
  );
}

SwipeclockUserBootstrap.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.oneOf(['workforcehub', 'providerportal', 'sysadmin'])
    .isRequired,
};

function cleanUpURL() {
  // Remove JWT from URL
  if (getUrlParameter('keep') !== '1') {
    removeQueryStringKeyFromURL('jwt');
  }

  // Remove other query string keys
  removeQueryStringKeyFromURL('impersonating');
  removeQueryStringKeyFromURL('loginType');
}

function useBootstrapWorkforceHub() {
  const navigate = useNavigate();

  const handleAgreements = useAgreements();
  const dispatch = useDispatch();
  const bootstrap = useCallback(
    async ({ masterData, billingData, metrics, user }) => {
      setPrivatePageTitle(masterData);
      dispatch(setUser(user));
      dispatch(setMasterData(masterData));
      dispatch(setMetricsData(metrics));

      // Gets the company and sets the navigation, site, and company name
      await setCompanyInformation(dispatch, masterData, user);
      await setLogonUrl(dispatch);
      await handleAgreements(user);

      // Finally we want to check if we need to redirect to billing
      // If so this will automatically disable the navigation and direct the user to the billing page
      if (billingData?.redirectToBilling) {
        navigate(`/admin/billing`);
        dispatch(
          setDisableNavigation({
            disabledTopAppBar: billingData.disableNavigation,
            hideNavigationDrawer: billingData.disableNavigation,
          })
        );
      }
    },
    []
  );
  return bootstrap;
}

function useBootstrapPartnerPortal() {
  const dispatch = useDispatch();
  const bootstrap = useCallback(async ({ masterData, user }) => {
    setPrivatePartnerPageTitle();
    dispatch(setUser(user));
    dispatch(setMasterData(masterData));
    // Gets the partner and sets the navigation, site,
    // It aslo gets login details and sets the usePartnerLogonUrl
    await setPartnerInformation(dispatch, masterData, user);
    await setLogonUrl(dispatch);
  }, []);
  return bootstrap;
}

function useBootstrapSysAdmin() {
  const dispatch = useDispatch();
  const bootstrap = useCallback(async ({ masterData, user }) => {
    setPrivatePartnerPageTitle();
    dispatch(setUser(user));
    dispatch(setMasterData(masterData));
    // Gets the admin info and sets the admin navigation, site,
    // Also gets login details and sets the usePartnerLogonUrl
    await setAdminInformation(dispatch, masterData, user);
    await setLogonUrl(dispatch);
  }, []);
  return bootstrap;
}
